import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createVNode as _createVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "sc-container-section" }
const _hoisted_2 = {
  class: "sc-row",
  role: "header"
}
const _hoisted_3 = {
  key: 0,
  class: "sc-form-buttons"
}
const _hoisted_4 = { class: "sc-row" }
const _hoisted_5 = { class: "post-disb-fields" }
const _hoisted_6 = { class: "form-row" }
const _hoisted_7 = { class: "form-row" }

import { ref, computed, onMounted, watch, nextTick } from 'vue';
import moment from 'moment';
import { DisbursementVM, DisbursementStatusIds, PostDisbursementChangeReasonTypes, PostDisbursementChangeModel } from '@/types/disbursements';
import { CertificationVM } from '@/types/certifications';
import { 
	usePostDisbursementChangeForm,
	CreatePostDisbursementChangeRequest 
} from '@/composables/disbursements/usePostDisbursementChangeForm';
import { useForm } from 'vee-validate';
import FormField from '@/components/v2/certifications/inputs/FormField.vue';
import * as DisbHelpers from '@/composables/disbursements/disbursementHelpers';
import { useDisbursementsStore } from '@/store/store/disbursementsStore';


export default /*@__PURE__*/_defineComponent({
  __name: 'PostDisbursementChangeForm',
  props: {
    disbursement: {},
    certification: {}
  },
  emits: ["cancel", "saved", "formChanged"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const store = useDisbursementsStore();

const props = __props;

const emit = __emit;

const editingPostDisbChangeId = ref<number | undefined>(undefined);
const mode = ref<'add' | 'edit'>('add');
const isEditMode = ref(false);
const isSaving = ref(false);
const showValidationErrors = ref(false);
const formKey = ref(0);
const isClearing = ref(false);

const disbursementRef = ref(props.disbursement);
const certificationRef = ref(props.certification);

const nextBusinessDay = computed(() => DisbHelpers.formatDateYYYYMMDD(props.certification.nextBusinessDay));
const initialValues = ref<Partial<CreatePostDisbursementChangeRequest>>({});

const formData = ref<Partial<CreatePostDisbursementChangeRequest>>({
	disbursementId: props.disbursement.details.id,
	disbursementAmount: 0,
	disbursementAmountDisplay: '',
	postDisbursementChangeReasonTypeId: undefined,
	settleDate: nextBusinessDay.value,
	effectiveDate: '',
	processingDate: '',
	disbursementMethodTypeId: props.disbursement.details.disbursementMethodTypeId,
});

const { formFields, validationSchema } = usePostDisbursementChangeForm({
	disbursement: disbursementRef,
	certification: certificationRef,
	formData: formData,
	mode: mode,
	isEditMode: isEditMode,
});

const { validate, setValues, setFieldValue } = useForm({
	validationSchema: validationSchema,
	initialValues: {
		disbursementAmount: 0,
		disbursementAmountDisplay: '',
		postDisbursementChangeReasonTypeId: undefined,
		settleDate: nextBusinessDay.value,
		effectiveDate: '',
		processingDate: '',
		disbursementMethodTypeId: props.disbursement.details.disbursementMethodTypeId,
	},
	validateOnMount: false,	
});

const formFieldsRow1 = computed(() => {
	return formFields.value.filter(f => 
		f.name === 'disbursementAmount' || 
		f.name === 'postDisbursementChangeReasonTypeId'
	);
});

const formFieldsRow2 = computed(() => {
	return formFields.value.filter(f => 
		f.name === 'processingDate' || 
		f.name === 'effectiveDate' || 
		f.name === 'settleDate' || 
		f.name === 'disbursementMethodTypeId'
	);
});

const hasFormData = computed(() => {	
	if (isEditMode.value) {
		if (!initialValues.value) return false;
				
		const hasChanges = 
			formData.value.postDisbursementChangeReasonTypeId !== initialValues.value.postDisbursementChangeReasonTypeId ||
			formData.value.processingDate !== initialValues.value.processingDate ||
			formData.value.effectiveDate !== initialValues.value.effectiveDate ||
			formData.value.settleDate !== initialValues.value.settleDate ||
			formData.value.disbursementMethodTypeId !== initialValues.value.disbursementMethodTypeId;
		
		return hasChanges;
	}
		
	const initialMethodTypeId = props.disbursement.details.disbursementMethodTypeId;
	return (
		(formData.value.disbursementAmount !== null && formData.value.disbursementAmount !== 0) ||
		formData.value.postDisbursementChangeReasonTypeId !== undefined ||
		(formData.value.settleDate !== 'mm/dd/yyyy' && formData.value.settleDate !== nextBusinessDay.value) ||
		(formData.value.processingDate !== '' && formData.value.processingDate !== undefined) ||
		(formData.value.effectiveDate !== '' && formData.value.effectiveDate !== undefined) ||        
		(formData.value.disbursementMethodTypeId !== undefined && 
			formData.value.disbursementMethodTypeId !== initialMethodTypeId)
	);
});

watch(hasFormData, (newValue) => {	
	emit('formChanged', newValue);
}, { immediate: true });

function handleFieldUpdate(fieldName: string, value: any) {
	formData.value[fieldName] = value;

	if (fieldName === 'disbursementAmount' && !isClearing.value) {
		store.updateFormAmount(props.disbursement.details.id, value);
  	}

	if (fieldName === 'disbursementAmount' && value > 0 && 
		props.disbursement.details.disbursementStatusTypeId === DisbursementStatusIds.POST_DISBURSEMENT_CANCELLATION
	) {
		
		formData.value.postDisbursementChangeReasonTypeId = PostDisbursementChangeReasonTypes.NOT_PROVIDED;
		setValues({
			...formData.value,
			postDisbursementChangeReasonTypeId: PostDisbursementChangeReasonTypes.NOT_PROVIDED
		});
	}
}

function handleFieldBlur(fieldName: string, event: FocusEvent | Event) {
  const value = formData.value[fieldName];

  if (fieldName === "processingDate" && value) {    
    calculateEffectiveDate(value);    
  }
}

function calculateEffectiveDate(processingDate: string | Date) {	
	if (!processingDate || !props.disbursement.details.disbursementDate) return;	
	
	const daysThreshold = props.certification.postDisbursementEffectiveDateDays || 45;
		
	const disbDateStr = getDateString(props.disbursement.details.disbursementDate);
		
	const procDateStr = typeof processingDate === 'string'
		? processingDate.split('T')[0]
		: processingDate.toISOString().split('T')[0];
	
	
	const diffDays = getDaysDifference(disbDateStr, procDateStr);
	const effectiveDateStr = diffDays <= daysThreshold ? disbDateStr : procDateStr;
	formData.value.effectiveDate = effectiveDateStr;
	setFieldValue('effectiveDate', effectiveDateStr);
}

function getDaysDifference(date1String: string, date2String: string): number {
  return Math.abs(moment(date2String).diff(moment(date1String), 'days'));
}

function getDateString(date: any): string {
  if (!date) return '';

  const m = moment(date, moment.ISO_8601, true);

  if (!m.isValid()) return '';

  return m.format('YYYY-MM-DD');
}

function handleClear() {  
	showValidationErrors.value = false;
	
	isClearing.value = true;
  
	formData.value = {
		disbursementId: props.disbursement.details.id,
		disbursementAmount: 0,
		disbursementAmountDisplay: '',
		postDisbursementChangeReasonTypeId: undefined,
		settleDate: nextBusinessDay.value,  
		effectiveDate: '',
		processingDate: '',
		disbursementMethodTypeId: props.disbursement.details.disbursementMethodTypeId,
	};
	
	setValues({
		disbursementAmount: 0,
		disbursementAmountDisplay: '',
		postDisbursementChangeReasonTypeId: undefined,
		settleDate: nextBusinessDay.value, 
		effectiveDate: '',
		processingDate: '',
		disbursementMethodTypeId: props.disbursement.details.disbursementMethodTypeId,
	});
	
	formKey.value++;  
	nextTick(() => {
		const originalAmount = props.disbursement.details.disbursementAmount || 0;
		store.updateFormAmount(props.disbursement.details.id, originalAmount);
		isClearing.value = false;
	});
	emit('formChanged', false);
}

onMounted(() => {    
	setValues({
		disbursementAmount: formData.value.disbursementAmount,
		disbursementAmountDisplay: formData.value.disbursementAmountDisplay,    
		postDisbursementChangeReasonTypeId: formData.value.postDisbursementChangeReasonTypeId,
		settleDate: formData.value.settleDate,
		effectiveDate: formData.value.effectiveDate,
		processingDate: formData.value.processingDate,
		disbursementMethodTypeId: formData.value.disbursementMethodTypeId,
	});
});

function setFormData(
	data: PostDisbursementChangeModel | CreatePostDisbursementChangeRequest, 
	postDisbChangeAmountLedgerId?: number
) {	
	if (postDisbChangeAmountLedgerId) {				
		isEditMode.value = true;
		editingPostDisbChangeId.value = postDisbChangeAmountLedgerId;
		mode.value = 'edit';
		
		formData.value = {
			disbursementId: props.disbursement.details.id,
			disbursementAmount: 'disbursementAmount' in data 
				? data.disbursementAmount 
				: props.disbursement.details.disbursementAmount,
			disbursementAmountDisplay: 'disbursementAmountDisplay' in data 
				? data.disbursementAmountDisplay 
				: props.disbursement.details.disbursementAmountDisplay,
			postDisbursementChangeReasonTypeId: data.postDisbursementChangeReasonTypeId,
			settleDate: DisbHelpers.formatDateYYYYMMDD(data.settleDate),
			effectiveDate: DisbHelpers.formatDateYYYYMMDD(data.effectiveDate),
			processingDate: DisbHelpers.formatDateYYYYMMDD(data.processingDate),
			disbursementMethodTypeId: data.disbursementMethodTypeId,
		};
				
		initialValues.value = { ...formData.value };
				
		setValues({
			disbursementAmount: formData.value.disbursementAmount,
			disbursementAmountDisplay: formData.value.disbursementAmountDisplay,
			postDisbursementChangeReasonTypeId: data.postDisbursementChangeReasonTypeId,
			settleDate: formData.value.settleDate,
			effectiveDate: formData.value.effectiveDate,
			processingDate: formData.value.processingDate,
			disbursementMethodTypeId: data.disbursementMethodTypeId,
		});
		
	} else {		
		isEditMode.value = false;
		editingPostDisbChangeId.value = undefined;
		mode.value = 'add';
		
		formData.value = {
			disbursementId: props.disbursement.details.id,
			disbursementAmount: 'disbursementAmount' in data ? data.disbursementAmount : 0,
			disbursementAmountDisplay: 'disbursementAmountDisplay' in data ? data.disbursementAmountDisplay : '',
			postDisbursementChangeReasonTypeId: 'postDisbursementChangeReasonTypeId' in data ? data.postDisbursementChangeReasonTypeId : undefined,
			settleDate: 'settleDate' in data ? DisbHelpers.formatDateYYYYMMDD(data.settleDate) : '',
			effectiveDate: 'effectiveDate' in data ? DisbHelpers.formatDateYYYYMMDD(data.effectiveDate) : '',
			processingDate: 'processingDate' in data ? DisbHelpers.formatDateYYYYMMDD(data.processingDate) : '',
			disbursementMethodTypeId: 'disbursementMethodTypeId' in data ? data.disbursementMethodTypeId : props.disbursement.details.disbursementMethodTypeId,
		};
		
		initialValues.value = { ...formData.value };
	}		
	emit('formChanged', hasFormData.value);
}

__expose({ 
	validateAndGetData: async () => {		
		showValidationErrors.value = true;
		const { valid } = await validate();
		
		if (!valid) {			
			return { valid: false };
		}
		
		if (!formData.value.disbursementId ||
				(!isEditMode.value && 
					(formData.value.disbursementAmount === null ||
					formData.value.disbursementAmount === undefined)
				) ||
				!formData.value.postDisbursementChangeReasonTypeId ||
				!formData.value.settleDate ||
				!formData.value.effectiveDate ||
				!formData.value.processingDate ||
				!formData.value.disbursementMethodTypeId) {			
			return { valid: false };
		}		
		return { valid: true };
	},
	
	getInitialValues: () => initialValues.value,
	getFormData: () => formData.value,
	getEditingPostDisbChangeId: () => editingPostDisbChangeId.value,
	isInEditMode: () => isEditMode.value,
	
	resetForm: () => {
		handleClear();
	},
	setFormData
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[0] || (_cache[0] = _createElementVNode("div", { class: "post-disb-header" }, " Post Disbursement Change ", -1)),
      (hasFormData.value && !isEditMode.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createElementVNode("button", {
              class: "sc-button cancel",
              onClick: handleClear
            }, "Clear")
          ]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("div", _hoisted_6, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(formFieldsRow1.value, (field) => {
            return (_openBlock(), _createElementBlock("div", {
              key: `${field.name}-${formKey.value}`,
              class: _normalizeClass(["form-field", field.width])
            }, [
              _createVNode(FormField, {
                isSubmitting: isSaving.value,
                isEditing: true,
                field: field,
                mode: mode.value,
                showErrors: showValidationErrors.value,
                modelValue: formData.value[field.name],
                "onUpdate:modelValue": [($event: any) => ((formData.value[field.name]) = $event), ($event: any) => (handleFieldUpdate(field.name, $event))]
              }, null, 8, ["isSubmitting", "field", "mode", "showErrors", "modelValue", "onUpdate:modelValue"])
            ], 2))
          }), 128))
        ]),
        _createElementVNode("div", _hoisted_7, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(formFieldsRow2.value, (field) => {
            return (_openBlock(), _createElementBlock("div", {
              key: `${field.name}-${formKey.value}`,
              class: _normalizeClass(["form-field", field.width])
            }, [
              _createVNode(FormField, {
                isSubmitting: isSaving.value,
                isEditing: true,
                field: field,
                mode: mode.value,
                showErrors: showValidationErrors.value,
                modelValue: formData.value[field.name],
                "onUpdate:modelValue": [($event: any) => ((formData.value[field.name]) = $event), ($event: any) => (handleFieldUpdate(field.name, $event))],
                onBlur: ($event: any) => (handleFieldBlur(field.name, $event))
              }, null, 8, ["isSubmitting", "field", "mode", "showErrors", "modelValue", "onUpdate:modelValue", "onBlur"])
            ], 2))
          }), 128))
        ])
      ])
    ])
  ]))
}
}

})